import React from 'react';
import debug from 'debug';
import { connect, ConnectedProps } from 'react-redux';
import { TRAVERSE_TREE_TARGETS } from 'client/components/common/BuildingBlocks/utils/common';
import { traverseTree } from 'client/components/common/BuildingBlocks/utils/traverse-tree';
import { Context } from 'client/components/common/ResponsiveProvider/Context';
import { BBModel, StorySymbols } from 'types/story';
import { ClientReducerState } from 'client/reducers';
import {
	ITraverseTreeOptions,
	ITraverseTreeOptionsListeners,
} from 'client/components/common/BuildingBlocks/utils/traverse-tree-types';

const log = debug('client:components:TreeRenderer');
const { Consumer } = Context;
const defaultProps = {
	// eslint-disable-next-line react/default-props-match-prop-types
	cardElements: [],
	symbols: {},
};

const mapStateToProps = (state: ClientReducerState) => ({
	state: state.editor.state,
	cardElements: state.card.elements!,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector> & {
	// state: EditableStateInfo;
	treeOptions:
		| { isCardTree: ITraverseTreeOptions['isCardTree']; target: ITraverseTreeOptions['target'] }
		| ({ isCardTree: ITraverseTreeOptions['isCardTree']; target: 'editor' } & ITraverseTreeOptionsListeners);
	elements: BBModel[];
	symbols?: StorySymbols;
};

const TreeRenderer = (props: Props) => {
	const { elements, treeOptions } = props;
	const isEditor = treeOptions.target === TRAVERSE_TREE_TARGETS.EDITOR;
	const extraProps = isEditor ? { state: props.state } : {};

	log(`%c${treeOptions.isCardTree ? 'cardElements' : 'storyElements'}`, 'font-weight: bold');

	return (
		<Consumer>
			{({ mediaQuery, currentMediaQuery }) =>
				typeof mediaQuery !== 'undefined' &&
				typeof currentMediaQuery !== 'undefined' &&
				traverseTree(elements, {
					...treeOptions,
					...extraProps,
					mediaQuery,
					currentMediaQuery,
					cardElements: props.cardElements,
					symbols: props.symbols,
				})
			}
		</Consumer>
	);
};

TreeRenderer.defaultProps = defaultProps;

function areEqual(prevProps, nextProps) {
	const isElementsEqual = prevProps.elements === nextProps.elements;
	const isSymbolsEqual = prevProps.symbols === nextProps.symbols;
	const isStateEqual =
		Object.keys(nextProps.state.source).some(key => nextProps.state.source[key] === prevProps.state.source[key]) &&
		prevProps.state.state === nextProps.state.state;

	if (!nextProps.treeOptions.isCardTree) {
		const isCardElementsEqual = prevProps.cardElements === nextProps.cardElements;
		return isStateEqual && isElementsEqual && isSymbolsEqual && isCardElementsEqual;
	}

	return isStateEqual && isElementsEqual && isSymbolsEqual;
}

export default connector(React.memo(TreeRenderer, areEqual));
